/**
 * Kurze Meldungen im Match: laufende Wellen und abgelehnte Aktionen.
 *
 * Fruher hing beides am Wirtschaftsfeld im HUD. Seit v0.29 steht das Feld
 * nicht mehr da - die Zahlen sind in die obere Statusleiste gewandert
 * (VISUAL_REFERENCE Abschnitt 16). Die Meldungen bleiben aber wichtig und
 * stehen jetzt mittig unter der Leiste, wo sie niemanden verdecken
 * (Abschnitt 48: die Mitte des Bildes gehoert dem Spiel).
 *
 * Alles hier ist reine Anzeige. Ob ein Kauf zustande kommt, entscheidet der
 * Server.
 */

import { useEffect, useState } from 'react';
import { t } from '../i18n/index.js';
import type { MatchNotice, MatchSnapshot } from '../net/MatchConnection.js';
import { formatGold, formatTier } from './economyView.js';
import { useHold } from './usePress.js';

/** So lange bleibt ein Hinweis wie "Nicht genug Gold" sichtbar. */
const NOTICE_VISIBLE_MS = 2500;

function noticeText(notice: MatchNotice): string {
  if (notice.kind === 'info' && notice.action === 'tier') {
    return `${t.army.tierTab} ${formatTier(notice.tier)} ${t.army.tierUnlocked}: -${formatGold(notice.cost)} ${t.economy.gold}`;
  }
  if (notice.kind === 'info') {
    return `${t.economy.waveSent}: -${formatGold(notice.cost)} ${t.economy.gold}, +${notice.incomeGain} ${t.economy.income}`;
  }

  // Shop und Skilltree haben eigene Gruende - sie stehen bei ihren Texten.
  const shopTexte = t.shop.blocked as Record<string, string>;
  const wirtschaftsTexte = t.economy.rejected as Record<string, string>;
  const grund = notice.reason ?? '';

  const vorspann =
    notice.action === 'item'
      ? `${t.shop.title}: `
      : notice.action === 'skill'
        ? `${t.skillTree.title}: `
        : '';

  const text = shopTexte[grund] ?? wirtschaftsTexte[grund] ?? t.economy.rejected.invalid_wave;
  return `${vorspann}${text}`;
}

export function MatchNotices({
  snapshot,
  onWatchStart,
  onWatchEnd,
  enemyView,
}: {
  snapshot: MatchSnapshot;
  /**
   * WATCH (Abschnitt 71): der Welle zusehen, solange der Finger liegt.
   * Seit dem 09.10.2026 ein Haltegriff wie der Knopf im Raster.
   */
  onWatchStart: () => void;
  onWatchEnd: () => void;
  enemyView: boolean;
}) {
  // Jeder neue Hinweis wird kurz eingeblendet und verschwindet dann von selbst.
  // Gemerkt wird nur, welcher Hinweis bereits abgelaufen ist.
  const [abgelaufeneId, setAbgelaufeneId] = useState(0);
  const notice = snapshot.notice;

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setAbgelaufeneId(notice.id), NOTICE_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const watchHalten = useHold(onWatchStart, onWatchEnd);

  const sichtbar: MatchNotice | null = notice && notice.id !== abgelaufeneId ? notice : null;
  const [naechsteWelle] = snapshot.wavesInTransit;

  return (
    <>
      {naechsteWelle !== undefined && (
        <div className="dl-transit">
          {t.army.arriving} {Math.ceil(naechsteWelle)}s
          {snapshot.wavesInTransit.length > 1 && ` (+${snapshot.wavesInTransit.length - 1})`}
          {!enemyView && (
            <button type="button" className="dl-watch-button" {...watchHalten}>
              {t.enemyView.watch}
            </button>
          )}
        </div>
      )}

      {sichtbar && (
        <div key={sichtbar.id} className={`dl-notice dl-notice--${sichtbar.kind}`} role="status">
          {noticeText(sichtbar)}
        </div>
      )}
    </>
  );
}
